// Tela de Faturamento por Cliente (ticket #19). Lista as Faturas e abre o detalhe:
// linhas de peça calculadas das Devoluções do período, ajustes de valor,
// emissão que congela + trava os Lotes, reabertura com justificativa.

import {
  calcularFatura,
  localizarOsCompensacao,
  mensagemOsCompensacao,
  osCompensavel,
  periodoSugerido,
  porDataEOs,
  quinzenaDoMes,
  situacaoFatura,
  type FaturaCalculada,
} from "../../domain/faturamento.ts";
import { formatReais } from "../../domain/money.ts";
import { formatData, hojeISO, isDataISO } from "../../domain/datas.ts";
import type { Fatura } from "../../domain/snapshot.ts";
import { tiposParaLancamento } from "../../domain/tipos-peca.ts";
import { vencimentoSugerido } from "../../domain/vencimento.ts";
import type { Store } from "../store.ts";
import {
  clear,
  el,
  linhaTotalTabela,
  openModal,
  rotuloColuna,
  tabelaPaginada, botao } from "../ui.ts";
import {
  abrirConfirmacao,
  barraCadastro,
  campo,
  campoObrigatorio,
  grupoCampos,
  inputData,
  inputTexto,
  select,
  telaLista,
} from "./_cadastro.ts";
import { criarAcaoComando, type AcaoComando } from "./_acao.ts";
import { toastErro } from "../toast.ts";
import { inputMoeda } from "./_input-moeda.ts";
import { abrirPlanilha } from "./_planilha.ts";
import { categoriaDoTipo, ordemPorCategoria, precosUsados, rotuloCompensacao } from "./_planilha-modelo.ts";
import { painelSelecao } from "./_multi-select.ts";
import { EstadoLista } from "./_estado-lista.ts";
import { buscaDaLista, filtroMultiplo } from "./_lista-filtros.ts";
import { podeEditarModulo } from "./_permissao.ts";

const ROTULO_SIT: Record<string, string> = {
  rascunho: "Rascunho",
  emitida: "Emitida",
  reaberta: "Reaberta",
  paga: "Paga",
};

function descrevePeriodo(f: Fatura): string {
  return `${formatData(f.periodo.de)} – ${formatData(f.periodo.ate)}`;
}

export function renderFaturamento(
  container: HTMLElement,
  store: Store,
): () => void {
  // filtros, busca e página num estado só (#93). Abre sem as pagas — e o
  // "Limpar Filtros" volta a esse padrão (antes marcava todas, até "Paga").
  // Sem coluna ativa: a ordem padrão é Cliente asc + Período desc, abaixo.
  const filtros = new EstadoLista({
    cliente: null as string[] | null,
    situacao: ["rascunho", "reaberta", "emitida"] as string[] | null,
  });
  const busca = buscaDaLista(filtros, "Buscar por número, cliente ou período…", () => render());
  const fCliente = filtroMultiplo(filtros, "cliente", "Cliente", () => render());
  const fSituacao = filtroMultiplo(filtros, "situacao", "Situação", () => render());

  const render = (): void => {
    clear(container);
    const snap = store.get();
    const podeGravar = podeEditarModulo(store, "financeiro");
    const cliNome = new Map(snap.clientes.map((c) => [c.id, c.nome]));
    const clientesAtivos = [...snap.clientes]
      .filter((c) => c.ativo)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    fCliente.atualizar(clientesAtivos.map((c) => ({ id: c.id, nome: c.nome })));
    fSituacao.atualizar([
      { id: "rascunho", nome: "Rascunho" },
      { id: "reaberta", nome: "Reaberta" },
      { id: "emitida", nome: "Emitida" },
      { id: "paga", nome: "Paga" },
    ]);
    const fClienteIds = fCliente.selecionados();
    const fSituacoes = fSituacao.selecionados();
    const faturas = snap.faturas
      .filter((f) => fClienteIds.size === 0 || fClienteIds.has(f.clienteId))
      .filter((f) => fSituacoes.has(situacaoFatura(snap, f)))
      .filter((f) =>
        busca.casa(
          f.numero ?? "rascunho",
          cliNome.get(f.clienteId),
          descrevePeriodo(f),
        ),
      )
      .sort((a, b) => {
        const c = (cliNome.get(a.clienteId) ?? "").localeCompare(
          cliNome.get(b.clienteId) ?? "",
          "pt-BR",
        );
        return c !== 0 ? c : b.periodo.de.localeCompare(a.periodo.de);
      });

    const massa = botao("secundario", {}, "Abrir em Massa");
    massa.addEventListener("click", () => {
      if (clientesAtivos.length === 0) {
        toastErro("Cadastre um cliente ativo antes de faturar.");
        return;
      }
      abrirEmMassa(store);
    });

    const barra = barraCadastro(
      [fCliente.element, fSituacao.element],
      "Nova Fatura",
      () => {
        if (clientesAtivos.length === 0) {
          toastErro("Cadastre um cliente ativo antes de faturar.");
          return;
        }
        abrirNova(store);
      },
      () => {
        filtros.limpar();
        render();
      },
      podeGravar ? [massa] : [],
      podeGravar,
    );

    if (faturas.length === 0) {
      container.append(
        telaLista(
          barra,
          busca.elemento(),
          el(
            "p",
            { class: "muted lista-vazia" },
            snap.faturas.length === 0
              ? "Nenhuma fatura ainda."
              : filtros.mensagemVazio({ nenhum: "Nenhuma fatura.", filtrado: "Nenhuma fatura com esses filtros." }),
          ),
        ),
      );
      return;
    }

    // Soma junto com o cálculo por linha, em vez de rodar calcularFatura()
    // de novo depois só pro total.
    let totalGeral = 0;
    const linhas = faturas.map((f) => {
      const sit = situacaoFatura(snap, f);
      const calc = calcularFatura(snap, f.id);
      totalGeral += calc.totalCentavos;
      const abrir = botao("secundario", {}, "Abrir");
      abrir.addEventListener("click", () => abrirFatura(store, f.id));
      return el(
        "tr",
        {},
        el("td", {}, el("b", {}, f.numero ?? "—")),
        el("td", {}, cliNome.get(f.clienteId) ?? "—"),
        el(
          "td",
          { class: "muted", "data-sort": f.periodo.de },
          descrevePeriodo(f),
        ),
        el(
          "td",
          { "data-sort": f.vencimento ?? "" },
          f.vencimento ? formatData(f.vencimento) : "—",
        ),
        el(
          "td",
          { class: "num", "data-sort": String(calc.totalCentavos) },
          formatReais(calc.totalCentavos),
        ),
        el(
          "td",
          {},
          el(
            "span",
            { class: `pill-fat pill-fat-${sit}` },
            ROTULO_SIT[sit] ?? sit,
          ),
        ),
        el("td", { class: "acoes" }, abrir),
      );
    });

    const colunas = [
      { t: "Número", w: 110 },
      "Cliente",
      { t: "Período", w: 190 },
      { t: "Vencimento", w: 120, ord: true as const },
      { t: "Total", w: 120, ord: true as const },
      { t: "Situação", w: 120 },
      { t: "", ord: false as const },
    ];
    // Deriva do próprio `colunas` em vez de contar na mão — a linha de total
    // não pode ficar desalinhada se uma coluna for adicionada/removida ali.
    const idxTotal = colunas.findIndex((c) => rotuloColuna(c) === "Total");

    // Linha de total, parte da própria tabela (tfoot) — soma o filtro
    // inteiro (`faturas`, já acumulado acima), não só a página que
    // `tabelaPaginada` mostra.
    const linhaTotal = linhaTotalTabela(
      colunas.length,
      idxTotal,
      `Total · ${faturas.length} fatura(s)`,
      [{ rotulo: "Total", texto: formatReais(totalGeral) }],
    );

    container.append(
      telaLista(
        barra,
        busca.elemento(),
        tabelaPaginada(
          "faturas",
          colunas,
          linhas,
          filtros.tabela,
          render,
          { linhaTotal },
        ),
      ),
    );
  };

  render();
  return store.subscribe(render);
}

// -------------------------------------------------------------- nova ----

function abrirNova(store: Store): void {
  const snap = store.get();
  const clientes = [...snap.clientes]
    .filter((c) => c.ativo)
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  const cliente = select(
    clientes.map((c) => [c.id, c.nome] as const),
    clientes[0]?.id ?? "",
  );
  const de = inputData();
  const ate = inputData();

  // Cliente quinzenal: o usuário escolhe 1ª ou 2ª quinzena do mês corrente.
  const quinzenaWrap = el("div");
  let quinzena: HTMLSelectElement | null = null;

  const aplicarPeriodo = (): void => {
    const c = snap.clientes.find((x) => x.id === cliente.value);
    if (!c) return;
    const p = quinzena
      ? quinzenaDoMes(quinzena.value === "2" ? 2 : 1, hojeISO())
      : periodoSugerido(c, hojeISO());
    de.value = p.de;
    ate.value = p.ate;
  };

  const sincronizar = (): void => {
    const c = snap.clientes.find((x) => x.id === cliente.value);
    clear(quinzenaWrap);
    quinzena = null;
    if (c?.cicloFechamento.tipo === "quinzenal") {
      // Abre na última quinzena FECHADA do mês corrente — dentro do mês só a 1ª
      // pode já ter terminado, então ela é sempre o padrão. Assim a tela nunca
      // sugere sozinha um período em aberto; faturar a quinzena em curso é uma
      // escolha explícita do usuário.
      quinzena = select(
        [
          ["1", "1ª Quinzena (dia 1 ao 15)"],
          ["2", "2ª Quinzena (dia 16 ao fim do mês)"],
        ],
        "1",
      );
      quinzena.addEventListener("change", aplicarPeriodo);
      quinzenaWrap.append(campo("Quinzena", quinzena));
    }
    aplicarPeriodo();
  };
  sincronizar();
  cliente.addEventListener("change", sincronizar);

  openModal("Nova Fatura", (fechar) => {
    const erro = el("p", { class: "form-erro", role: "alert" });
    const cancelar = botao("secundario", {}, "Cancelar");
    cancelar.addEventListener("click", fechar);
    const ok = botao("primario", { type: "submit" }, "Criar Rascunho");
    const form = el(
      "form",
      { class: "form-modal" },
      campo("Cliente", cliente),
      quinzenaWrap,
      el(
        "div",
        { class: "grid-2" },
        campo("Início do período", de),
        campo("Fim do período", ate),
      ),
      el(
        "p",
        { class: "muted" },
        "O período vem do ciclo de fechamento do cliente — ajuste se precisar.",
      ),
      erro,
      el("div", { class: "modal-actions" }, cancelar, ok),
    );
    form.addEventListener("submit", (ev) => {
      ev.preventDefault();
      erro.textContent = "";
      ok.disabled = true;
      const idsAntes = new Set(store.get().faturas.map((f) => f.id));
      criarAcaoComando(store, erro)(
        {
          agregado: "fatura",
          cmd: {
            tipo: "criar",
            clienteId: cliente.value,
            periodo: { de: de.value, ate: ate.value },
          },
        },
        "Rascunho de fatura criado.",
        () => {
          fechar();
          const nova = store.get().faturas.find((f) => !idsAntes.has(f.id));
          if (nova) abrirFatura(store, nova.id);
        },
      ).finally(() => {
        ok.disabled = false;
      });
    });
    return form;
  });
}

// -------------------------------------------------------------- em massa ----

function abrirEmMassa(store: Store): void {
  const snap = store.get();
  const cliNome = new Map(snap.clientes.map((c) => [c.id, c.nome]));
  const clientesAtivos = [...snap.clientes]
    .filter((c) => c.ativo)
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  openModal("Abrir Faturas em Massa", (fechar) => {
    const de = inputData();
    const ate = inputData();
    // sugestão de partida: mês anterior inteiro — ajuste fino fica com quem lança
    const sugestao = periodoSugerido({ cicloFechamento: { tipo: "mensal" } }, hojeISO());
    de.value = sugestao.de;
    ate.value = sugestao.ate;

    const erro = el("p", { class: "form-erro", role: "alert" });
    const abrirBtn = botao(
      "primario",
      {},
      "Abrir",
    ) as HTMLButtonElement;
    abrirBtn.disabled = true;

    const painel = painelSelecao({
      itens: clientesAtivos.map((c) => ({ id: c.id, nome: c.nome })),
      buscaPlaceholder: "Buscar cliente…",
      aoMudar: () => atualizar(),
    });
    const contador = el("span", { class: "selecao-contador" });
    const atualizar = (): void => {
      const n = painel.selecionados().length;
      contador.textContent = `${n} de ${clientesAtivos.length} selecionado(s)`;
      abrirBtn.textContent = n > 0 ? `Abrir ${n} Fatura(s)` : "Abrir";
      abrirBtn.disabled = n === 0;
    };
    atualizar();

    const corpo = el("div", { class: "form-modal" });

    const mostrarResumo = (
      abertas: number,
      puladas: { clienteId: string; motivo: string }[],
    ): void => {
      clear(corpo);
      corpo.append(
        el("p", { class: "aviso aviso-sucesso" }, `${abertas} fatura(s) aberta(s).`),
      );
      if (puladas.length > 0) {
        corpo.append(
          el(
            "p",
            { class: "muted" },
            `${puladas.length} cliente(s) pulado(s), sem nova fatura:`,
          ),
          el(
            "table",
            { class: "mini" },
            el("tbody", {}, ...puladas.map((p) =>
              el(
                "tr",
                {},
                el("td", {}, cliNome.get(p.clienteId) ?? p.clienteId),
                el("td", { class: "muted" }, p.motivo),
              ),
            )),
          ),
        );
      }
      const ok = botao("primario", {}, "Fechar");
      ok.addEventListener("click", fechar);
      corpo.append(el("div", { class: "modal-actions" }, ok));
    };

    abrirBtn.addEventListener("click", () => {
      erro.textContent = "";
      const clienteIds = painel.selecionados();
      if (clienteIds.length === 0) return;
      const periodo = { de: de.value, ate: ate.value };
      if (!isDataISO(periodo.de) || !isDataISO(periodo.ate) || periodo.de > periodo.ate) {
        erro.textContent = "Informe um período válido.";
        return;
      }
      abrirBtn.disabled = true;
      // Um comando só (transição atômica no servidor). O resumo — quem abriu,
      // quem foi pulado — sai do diff do Snapshot: Cliente marcado que não
      // ganhou Fatura nova já tinha uma sobrepondo o período.
      const idsAntes = new Set(store.get().faturas.map((f) => f.id));
      void criarAcaoComando(store, erro)(
        { agregado: "fatura", cmd: { tipo: "criarEmMassa", clienteIds, periodo } },
        null,
        () => {
          const depois = store.get().faturas;
          const abriuPara = new Set(
            depois.filter((f) => !idsAntes.has(f.id)).map((f) => f.clienteId),
          );
          const seSobrepoe = (f: Fatura): boolean =>
            f.periodo.de <= periodo.ate && periodo.de <= f.periodo.ate;
          const puladas = clienteIds
            .filter((id) => !abriuPara.has(id))
            .map((clienteId) => ({
              clienteId,
              motivo: depois.some(
                (f) => f.clienteId === clienteId && seSobrepoe(f) && idsAntes.has(f.id),
              )
                ? "Já tinha fatura nesse período."
                : "Não foi possível abrir.",
            }));
          mostrarResumo(abriuPara.size, puladas);
        },
        () => atualizar(),
      );
    });

    const cancelar = botao("secundario", {}, "Cancelar");
    cancelar.addEventListener("click", fechar);

    corpo.append(
      el(
        "p",
        { class: "muted" },
        "O mesmo período vale para todos os clientes marcados. Cliente que já tiver fatura nesse período é pulado.",
      ),
      el(
        "div",
        { class: "campo selecao-campo" },
        el(
          "div",
          { class: "selecao-campo-cab" },
          el("span", {}, "Clientes"),
          contador,
        ),
        painel.element,
      ),
      el(
        "div",
        { class: "grid-2" },
        campo("Início do período", de),
        campo("Fim do período", ate),
      ),
      erro,
      el("div", { class: "modal-actions" }, cancelar, abrirBtn),
    );
    return corpo;
  });
}

// -------------------------------------------------------------- detalhe ----

export function abrirFatura(store: Store, faturaId: string): void {
  openModal(
    "Fatura",
    (fechar) => {
      const raiz = el("div", { class: "fatura-detalhe" });
      // estado da tela enquanto este modal está aberto: toda abertura começa
      // com a Compensação fechada
      const tela: EstadoTelaFatura = { compensacaoAberta: false };
      const redesenhar = (): void => {
        const snap = store.get();
        const f = snap.faturas.find((x) => x.id === faturaId);
        clear(raiz);
        raiz.append(
          f
            ? corpoFatura(store, calcularFatura(snap, f.id), fechar, redesenhar, tela)
            : el("p", { class: "muted" }, "Fatura removida."),
        );
      };
      redesenhar();
      return raiz;
    },
    "largo",
  );
}

interface EstadoTelaFatura {
  compensacaoAberta: boolean;
}

function corpoFatura(
  store: Store,
  calc: FaturaCalculada,
  fechar: () => void,
  redesenhar: () => void,
  tela: EstadoTelaFatura,
): HTMLElement {
  const snap = store.get();
  const podeGravar = podeEditarModulo(store, "financeiro");
  const f = calc.fatura;
  const rascunho = !calc.emitida;
  const cliente = snap.clientes.find((c) => c.id === f.clienteId);
  const tipoNome = new Map(snap.tiposPeca.map((t) => [t.id, t.nome]));
  const categoriaDe = categoriaDoTipo(snap);
  /** linhas na ordem da Planilha: por Categoria e, dentro dela, por Tipo de Peça. */
  const ordem = ordemPorCategoria(snap);
  const linhasOrdenadas = [...calc.linhas].sort((a, b) => ordem(a.tipoId, b.tipoId));
  const sit = situacaoFatura(snap, f);
  const erro = el("p", { class: "form-erro", role: "alert" });
  // toda ação deste painel: aplica, redesenha e mostra o erro aqui
  const acao = criarAcaoComando(store, erro, redesenhar);

  // --- cabeçalho ---
  const head = el(
    "div",
    { class: "fatura-head" },
    el(
      "div",
      {},
      el("h3", {}, cliente?.nome ?? "—"),
      el(
        "span",
        { class: `pill-fat pill-fat-${sit}` },
        f.numero ? `${ROTULO_SIT[sit]} · ${f.numero}` : ROTULO_SIT[sit],
      ),
    ),
  );

  if (rascunho) {
    const de = inputData(f.periodo.de);
    const ate = inputData(f.periodo.ate);
    de.disabled = !podeGravar;
    ate.disabled = !podeGravar;
    const salvarPeriodo = (): void => {
      if (!isDataISO(de.value) || !isDataISO(ate.value)) return;
      if (de.value === f.periodo.de && ate.value === f.periodo.ate) return;
      acao(
        {
          agregado: "fatura",
          cmd: {
            tipo: "editarPeriodo",
            faturaId: f.id,
            periodo: { de: de.value, ate: ate.value },
          },
        },
        "Período atualizado.",
      );
    };
    de.addEventListener("change", salvarPeriodo);
    ate.addEventListener("change", salvarPeriodo);
    head.append(
      el(
        "div",
        { class: "grid-2 fatura-periodo" },
        campo("Início", de),
        campo("Fim", ate),
      ),
    );
  } else {
    head.append(
      el("p", { class: "muted" }, `Período: ${descrevePeriodo(f)}`),
    );
  }

  // Vencimento (#40): só existe depois de emitida, e pode ter ficado em branco
  // se o Cliente não tem Dia de Vencimento cadastrado.
  if (f.emitidaEm != null) {
    head.append(
      el(
        "p",
        { class: "muted" },
        `Vencimento: ${f.vencimento == null ? "não informado" : formatData(f.vencimento)}`,
      ),
    );
  }

  // --- alerta de preço pendente ---
  const avisos: Node[] = [];
  if (calc.tiposPendentes.length > 0) {
    avisos.push(
      el(
        "p",
        { class: "form-erro" },
        "Sem preço em alguma data do período (ou da OS de uma compensação): " +
          calc.tiposPendentes
            .map((id) => tipoNome.get(id) ?? id)
            .join(", ") +
          ". Precifique na Tabela de Preços antes de emitir.",
      ),
    );
  }

  // --- linhas de peça (Categoria › Tipo) ---
  const corpoLinhas: HTMLElement[] = [];
  for (const l of linhasOrdenadas) {
    corpoLinhas.push(
      el(
        "tr",
        {},
        el("td", { class: "muted" }, categoriaDe(l.tipoId)),
        el("td", {}, tipoNome.get(l.tipoId) ?? l.tipoId),
        el(
          "td",
          { class: "num" },
          String(l.quantidade),
          l.deRetidas > 0
            ? el("small", { class: "muted" }, ` (${l.deRetidas} de retidas)`)
            : null,
        ),
        el(
          "td",
          { class: "num" },
          l.pendente
            ? "—"
            : el(
                "span",
                { class: "planilha-precos" },
                ...precosUsados(l.precosCentavos, l.unitarioCentavos).map((p) => el("span", {}, formatReais(p))),
              ),
        ),
        el("td", { class: "num" }, l.pendente ? "—" : formatReais(l.subtotalCentavos)),
        el("td", {}, ""),
      ),
    );
    for (const aj of l.ajustes) {
      corpoLinhas.push(
        linhaAjuste(f, aj, tipoNome.get(l.tipoId) ?? "", rascunho && podeGravar, acao),
      );
    }
  }
  for (const aj of calc.ajustesLivres) {
    corpoLinhas.push(linhaAjuste(f, aj, null, rascunho && podeGravar, acao));
  }
  if (corpoLinhas.length === 0) {
    corpoLinhas.push(
      el(
        "tr",
        {},
        el("td", { class: "muted", colspan: "6" }, "Nenhuma devolução no período."),
      ),
    );
  }

  const tabela = el(
    "table",
    { class: "mini fatura-linhas" },
    el(
      "thead",
      {},
      el(
        "tr",
        {},
        el("th", {}, "Categoria"),
        el("th", {}, "Tipo de peça"),
        el("th", { class: "r" }, "Qtd"),
        el("th", { class: "r" }, "Unitário"),
        el("th", { class: "r" }, "Subtotal"),
        el("th", {}, ""),
      ),
    ),
    el("tbody", {}, ...corpoLinhas),
  );

  // --- adicionar ajuste (só rascunho) ---
  const blocoAjuste: Node[] = [];
  if (rascunho && podeGravar) {
    const desc = inputTexto("Descrição (ex.: Cortesia)");
    const valor = inputMoeda({ ariaLabel: "Valor do ajuste" });
    const sinal = select(
      [
        ["-", "Desconto"],
        ["+", "Acréscimo"],
      ],
      "-",
    );
    const alvo = select(
      [
        ["", "Linha Livre"],
        ...linhasOrdenadas.map(
          (l) =>
            [
              l.tipoId,
              `${categoriaDe(l.tipoId)} · ${tipoNome.get(l.tipoId) ?? l.tipoId}`,
            ] as const,
        ),
      ],
      "",
    );
    const add = botao("secundario", {}, "Adicionar Ajuste");
    add.addEventListener("click", () => {
      erro.textContent = "";
      const centavos = valor.centavos();
      if (centavos == null || centavos === 0) {
        erro.textContent = "Informe o valor do ajuste.";
        return;
      }
      const assinado = sinal.value === "-" ? -centavos : centavos;
      acao(
        {
          agregado: "fatura",
          cmd: {
            tipo: "adicionarAjuste",
            faturaId: f.id,
            descricao: desc.value,
            valorCentavos: assinado,
            tipoId: alvo.value || null,
          },
        },
        "Ajuste adicionado.",
      );
    });
    blocoAjuste.push(
      el(
        "div",
        { class: "fatura-subform fatura-ajuste-form" },
        campoObrigatorio("Ajuste", desc),
        campo("Onde", alvo),
        campo("Tipo", sinal),
        campo("Valor", valor.element),
        add,
      ),
    );
  }

  // --- totais ---
  const totais = el(
    "table",
    { class: "mini fatura-totais" },
    el(
      "tbody",
      {},
      el(
        "tr",
        {},
        el("td", {}, "Subtotal das peças"),
        el("td", { class: "r num" }, formatReais(calc.subtotalPecasCentavos)),
      ),
      calc.ajustesCentavos !== 0
        ? el(
            "tr",
            {},
            el("td", {}, "Ajustes"),
            el("td", { class: "r num" }, formatReais(calc.ajustesCentavos)),
          )
        : null,
      el(
        "tr",
        { class: "fatura-total" },
        el("td", {}, el("b", {}, "Total")),
        el("td", { class: "r num" }, el("b", {}, formatReais(calc.totalCentavos))),
      ),
    ),
  );

  // --- ações ---
  const acoes = el("div", { class: "modal-actions" });
  const fecharBtn = botao("secundario", {}, "Fechar");
  fecharBtn.addEventListener("click", fechar);
  const planilha = botao(
    "secundario",
    {},
    "Planilha de Fechamento",
  );
  planilha.addEventListener("click", () => abrirPlanilha(store, f.id));

  // Compensação de Peças (ADR-0031): o botão abre/fecha a seção — fechada,
  // só o botão com a contagem. Emitida, só mostra o que foi compensado.
  const podeCompensar = rascunho && podeGravar;
  const compensacaoAberta = tela.compensacaoAberta;
  const compensacao: Node[] = [];
  const botoesCompensacao: Node[] = [];
  if (podeCompensar || f.compensacoes.length > 0) {
    const n = f.compensacoes.length;
    const alternar = botao(
      "secundario",
      { "aria-expanded": String(compensacaoAberta) },
      n > 0 ? `Compensação Peças (${n})` : "Compensação Peças",
    );
    alternar.addEventListener("click", () => {
      tela.compensacaoAberta = !compensacaoAberta;
      redesenhar();
    });
    botoesCompensacao.push(alternar);
    if (compensacaoAberta) {
      compensacao.push(secaoCompensacao(store, f, tipoNome, podeCompensar, acao, erro));
    }
  }

  if (rascunho && podeGravar) {
    const excluir = botao("perigo", {}, "Excluir");
    excluir.addEventListener("click", () => {
      abrirConfirmacao({
        titulo: "Excluir este Rascunho?",
        mensagem:
          "O rascunho de fatura sai do sistema. As OS do período voltam a ficar livres. Não dá para desfazer.",
        rotuloConfirmar: "Excluir",
        perigo: true,
        aoConfirmar: () =>
          acao(
            { agregado: "fatura", cmd: { tipo: "excluir", faturaId: f.id } },
            "Rascunho excluído.",
            fechar,
          ),
      });
    });
    const emitir = botao(
      "primario",
      {},
      "Emitir Fatura",
    );
    emitir.disabled = calc.tiposPendentes.length > 0;
    emitir.addEventListener("click", () => abrirEmitir(store, f.id, redesenhar));
    acoes.append(fecharBtn, ...botoesCompensacao, planilha, excluir, emitir);
  } else if (!rascunho) {
    // A baixa NÃO acontece mais aqui (ADR-0011): emitir criou uma Conta a
    // Receber, e é lá que se registra o recebimento. A Fatura só espelha.
    const irParaConta = botao("secundario", {}, "Ver em Contas a Receber");
    irParaConta.addEventListener("click", () => {
      location.hash = "#/contas-receber";
      fechar();
    });
    acoes.append(fecharBtn, ...botoesCompensacao, planilha, irParaConta);
    if (podeGravar) {
      const reabrir = botao("perigo", {}, "Reabrir");
      reabrir.addEventListener("click", () => abrirReabrir(store, f.id, redesenhar));
      acoes.append(reabrir);
    }
  } else {
    acoes.append(fecharBtn, ...botoesCompensacao, planilha);
  }

  // --- histórico de reaberturas ---
  const hist: Node[] = [];
  if (f.reaberturas.length > 0) {
    hist.push(
      el(
        "details",
        { class: "fatura-hist" },
        el("summary", {}, `Reaberturas (${f.reaberturas.length})`),
        el(
          "ul",
          {},
          ...f.reaberturas.map((r) =>
            el("li", {}, `${formatData(r.em)} — ${r.justificativa}`),
          ),
        ),
      ),
    );
  }

  return el(
    "div",
    {},
    head,
    ...avisos,
    el("div", { class: "table-wrap" }, tabela),
    ...blocoAjuste,
    totais,
    ...hist,
    ...compensacao,
    erro,
    acoes,
  );
}

/**
 * A seção de Compensação de Peças (ADR-0031): a lista do que já foi
 * compensado e, com a fatura em rascunho, o formulário — uma OS, o motivo e
 * quantas linhas de peça + quantidade forem precisas.
 */
function secaoCompensacao(
  store: Store,
  f: Fatura,
  tipoNome: ReadonlyMap<string, string>,
  editavel: boolean,
  acao: AcaoComando,
  erro: HTMLElement,
): HTMLElement {
  const secao = el(
    "section",
    { class: "fatura-compensacao", "aria-label": "Compensação de Peças" },
    el("h3", {}, "Compensação de Peças"),
  );

  if (f.compensacoes.length > 0) {
    const linhas = [...f.compensacoes]
      .sort(porDataEOs)
      .map((c) => {
        const rm = botao(
          { variante: "perigo", tamanho: "compacto" },
          { "aria-label": `Remover a Compensação da OS ${c.os}` },
          "×",
        );
        rm.addEventListener("click", () =>
          acao(
            { agregado: "fatura", cmd: { tipo: "removerCompensacao", faturaId: f.id, compensacaoId: c.id } },
            "Compensação removida.",
          ),
        );
        const pecas = c.pecas
          .map((p) => `${p.quantidade > 0 ? "+" : ""}${p.quantidade} ${tipoNome.get(p.tipoId) ?? p.tipoId}`)
          .join(", ");
        return el(
          "tr",
          {},
          el("td", { class: "num" }, formatData(c.data)),
          el("td", {}, rotuloCompensacao(c.os)),
          el("td", {}, pecas),
          el("td", { class: "muted" }, c.motivo),
          el("td", {}, editavel ? rm : ""),
        );
      });
    secao.append(
      el(
        "div",
        { class: "table-wrap" },
        el(
          "table",
          { class: "mini" },
          el(
            "thead",
            {},
            el("tr", {}, el("th", {}, "Data"), el("th", {}, "OS"), el("th", {}, "Peças"), el("th", {}, "Motivo"), el("th", {}, "")),
          ),
          el("tbody", {}, ...linhas),
        ),
      ),
    );
  } else if (!editavel) {
    secao.append(el("p", { class: "muted" }, "Nenhuma compensação."));
  }
  if (!editavel) return secao;

  // --- OS: a data vem dela quando está no sistema; senão, é digitada
  const os = inputTexto("Ex.: 1", "", { inputmode: "numeric", "aria-label": "OS" });
  const data = inputData();
  const campoDataOS = campoObrigatorio("Data da OS", data);
  const dica = el("p", { class: "fatura-compensacao-dica", "aria-live": "polite" });
  /** O número digitado; `NaN` quando vazio (o domínio recusa com "Informe a OS"). */
  const numeroOS = (): number => (os.value.trim() === "" ? NaN : Number(os.value.trim()));
  const buscarOS = (): void => {
    const n = numeroOS();
    const onde = Number.isInteger(n) && n > 0 ? localizarOsCompensacao(store.get(), f.id, n) : null;
    campoDataOS.hidden = onde?.tipo !== "foraDoSistema";
    dica.textContent = onde ? mensagemOsCompensacao(n, onde) : "";
    dica.classList.toggle("form-erro", onde != null && !osCompensavel(onde));
  };
  os.addEventListener("input", buscarOS);
  buscarOS();
  const motivo = inputTexto("Ex.: faltaram 2 protetores na OS 1");

  // --- peças: quantas linhas de peça + quantidade forem precisas
  const tipos = tiposParaLancamento(store.get(), f.clienteId);
  const pecasEl = el("div", { class: "fatura-compensacao-pecas" });
  const linhasPeca: { tipo: HTMLSelectElement; qtd: HTMLInputElement }[] = [];
  const adicionarPeca = (): void => {
    const tipo = select([["", "Escolha a peça"], ...tipos.map((t) => [t.id, t.nome] as const)], "");
    tipo.setAttribute("aria-label", "Peça");
    const qtd = el("input", {
      type: "number",
      step: "1",
      placeholder: "Qtd.",
      "aria-label": "Quantidade",
    }) as HTMLInputElement;
    const par = { tipo, qtd };
    const rm = botao({ variante: "secundario", tamanho: "compacto" }, { "aria-label": "Remover Peça" }, "×");
    const linha = el("div", { class: "fatura-compensacao-peca" }, tipo, qtd, rm);
    rm.addEventListener("click", () => {
      linhasPeca.splice(linhasPeca.indexOf(par), 1);
      linha.remove();
    });
    linhasPeca.push(par);
    pecasEl.append(linha);
  };
  adicionarPeca();
  const maisPeca = botao("link", {}, "+ Adicionar Peça");
  maisPeca.addEventListener("click", () => {
    adicionarPeca();
    linhasPeca.at(-1)?.tipo.focus();
  });

  const adicionar = botao("secundario", {}, "Adicionar Compensação");
  adicionar.addEventListener("click", () => {
    erro.textContent = "";
    // linha em branco (sem peça e sem quantidade) é ignorada
    const preenchidas = linhasPeca.filter((p) => p.tipo.value !== "" || p.qtd.value.trim() !== "");
    if (preenchidas.some((p) => p.tipo.value === "")) {
      erro.textContent = "Escolha a peça em cada linha preenchida.";
      return;
    }
    acao(
      {
        agregado: "fatura",
        cmd: {
          tipo: "adicionarCompensacao",
          faturaId: f.id,
          os: numeroOS(),
          data: isDataISO(data.value) ? data.value : null,
          motivo: motivo.value,
          pecas: preenchidas.map((p) => ({
            tipoId: p.tipo.value,
            quantidade: p.qtd.value.trim() === "" ? NaN : Number(p.qtd.value),
          })),
        },
      },
      "Compensação adicionada.",
    );
  });

  secao.append(
    el(
      "div",
      { class: "fatura-subform fatura-compensacao-form" },
      el("div", { class: "fatura-compensacao-os" }, campoObrigatorio("OS", os), campoDataOS, campoObrigatorio("Motivo", motivo)),
      dica,
      grupoCampos(
        "Peças",
        { obrigatorio: true, hint: "Quantidade negativa tira peças cobradas a mais. O preço é o da data da OS." },
        pecasEl,
        el("div", {}, maisPeca),
      ),
      el("div", {}, adicionar),
    ),
  );
  return secao;
}

function linhaAjuste(
  f: Fatura,
  aj: { id: string; descricao: string; valorCentavos: number },
  tipoNome: string | null,
  rascunho: boolean,
  acao: AcaoComando,
): HTMLElement {
  const rm = botao({ variante: "perigo", tamanho: "compacto" }, {}, "×");
  rm.addEventListener("click", () => {
    acao(
      {
        agregado: "fatura",
        cmd: { tipo: "removerAjuste", faturaId: f.id, ajusteId: aj.id },
      },
      "Ajuste removido.",
    );
  });
  return el(
    "tr",
    { class: "fatura-linha-ajuste" },
    el(
      "td",
      { class: "muted", colspan: "2" },
      tipoNome ? `↳ ${aj.descricao}` : aj.descricao,
    ),
    el("td", {}, ""),
    el("td", {}, ""),
    el("td", { class: "num" }, formatReais(aj.valorCentavos)),
    el("td", {}, rascunho ? rm : ""),
  );
}

function abrirEmitir(
  store: Store,
  faturaId: string,
  aoEmitir: () => void,
): void {
  openModal("Emitir Fatura", (fechar) => {
    const snap = store.get();
    const f = snap.faturas.find((x) => x.id === faturaId);
    if (!f) {
      fechar();
      return el("div", {});
    }
    // Vencimento pré-preenchido pelo Dia de Vencimento do Cliente (#40) e
    // ajustável à mão. Sem Dia cadastrado, abre em branco — e em branco
    // significa emitir sem vencimento, não "adivinhe".
    const cli = snap.clientes.find((c) => c.id === f.clienteId);
    const sugestao = cli ? vencimentoSugerido(cli, f.periodo.ate) : null;
    const venc = inputData(sugestao ?? "");

    const erro = el("p", { class: "form-erro", role: "alert" });
    const voltar = botao("secundario", {}, "Voltar");
    voltar.addEventListener("click", fechar);
    const ok = botao("primario", { type: "submit" }, "Emitir Fatura");

    const form = el(
      "form",
      { class: "form-modal" },
      el(
        "p",
        { class: "muted" },
        "Emitir dá o número da fatura, congela os valores e trava as OS do período. " +
          "Para mudar depois é preciso reabrir com justificativa.",
      ),
      campo("Vencimento", venc),
      el(
        "p",
        { class: "muted", style: "margin:-6px 0 0" },
        "Em branco: emite sem data de vencimento.",
      ),
      erro,
      el("div", { class: "modal-actions" }, voltar, ok),
    );
    form.addEventListener("submit", (ev) => {
      ev.preventDefault();
      const v = venc.value.trim();
      if (v !== "" && !isDataISO(v)) {
        erro.textContent = "Vencimento inválido.";
        return;
      }
      criarAcaoComando(store, erro)(
        {
          agregado: "fatura",
          cmd: { tipo: "emitir", faturaId, vencimento: v === "" ? null : v },
        },
        "Fatura emitida.",
        () => {
          fechar();
          aoEmitir();
        },
      );
    });
    return form;
  });
}

function abrirReabrir(
  store: Store,
  faturaId: string,
  aoReabrir: () => void,
): void {
  openModal("Reabrir Fatura", (fechar) => {
    const erro = el("p", { class: "form-erro", role: "alert" });
    const motivo = el("textarea", {
      rows: "3",
      placeholder: "Por que a fatura precisa ser reaberta?",
    }) as HTMLTextAreaElement;
    const voltar = botao("secundario", {}, "Voltar");
    voltar.addEventListener("click", fechar);
    const ok = botao("primario", { type: "submit" }, "Reabrir");
    const form = el(
      "form",
      { class: "form-modal" },
      el(
        "p",
        { class: "muted" },
        "A fatura volta a rascunho e as OS voltam a “devolvido”. A justificativa fica registrada.",
      ),
      campoObrigatorio("Justificativa", motivo),
      erro,
      el("div", { class: "modal-actions" }, voltar, ok),
    );
    form.addEventListener("submit", (ev) => {
      ev.preventDefault();
      criarAcaoComando(store, erro)(
        {
          agregado: "fatura",
          cmd: { tipo: "reabrir", faturaId, justificativa: motivo.value },
        },
        "Fatura reaberta.",
        () => {
          fechar();
          aoReabrir();
        },
      );
    });
    return form;
  });
}
